import * as React from "react";
import { ResizableImage } from "tiptap-extension-resizable-image";
import { prosemirrorJSONToYXmlFragment } from "y-prosemirror";
import type * as Y from "yjs";

import { reportTiptapContentError } from "@/components/_organisms/tiptap/report-tiptap-content-error";
import { Link } from "@/components/_organisms/tiptap/tiptap-extension/link-extension";
import { Selection } from "@/components/_organisms/tiptap/tiptap-extension/selection-extension";
import { ArrowLeftIcon } from "@/components/_organisms/tiptap/tiptap-icons/arrow-left-icon";
import { HighlighterIcon } from "@/components/_organisms/tiptap/tiptap-icons/highlighter-icon";
import { LinkIcon } from "@/components/_organisms/tiptap/tiptap-icons/link-icon";
import { ImageUploadNode } from "@/components/_organisms/tiptap/tiptap-node/image-upload-node/image-upload-node-extension";
import { PdfUploadNode } from "@/components/_organisms/tiptap/tiptap-node/pdf-upload-node/pdf-upload-node-extension";
import { BackgroundColorButton } from "@/components/_organisms/tiptap/tiptap-ui/background-color-button/background-color-button";
import FontSizeDropdown from "@/components/_organisms/tiptap/tiptap-ui/font-size-dropdown/font-size-dropdown";
import { HeadingDropdownMenu } from "@/components/_organisms/tiptap/tiptap-ui/heading-dropdown-menu";
import {
  HighlightContent,
  HighlighterButton,
  HighlightPopover,
} from "@/components/_organisms/tiptap/tiptap-ui/highlight-popover";
import { ImageUploadButton } from "@/components/_organisms/tiptap/tiptap-ui/image-upload-button";
import LineHeightDropdown from "@/components/_organisms/tiptap/tiptap-ui/line-height-dropdown/line-height-dropdown";
import { LinkButton, LinkContent, LinkPopover } from "@/components/_organisms/tiptap/tiptap-ui/link-popover";
import { ListDropdownMenu } from "@/components/_organisms/tiptap/tiptap-ui/list-dropdown-menu";
import { MarkButton } from "@/components/_organisms/tiptap/tiptap-ui/mark-button";
import { NodeButton } from "@/components/_organisms/tiptap/tiptap-ui/node-button";
import PdfUploadButton from "@/components/_organisms/tiptap/tiptap-ui/pdf-upload-button/pdf-upload-button";
import { TextAlignButton } from "@/components/_organisms/tiptap/tiptap-ui/text-align-button";
import { TextColorButton } from "@/components/_organisms/tiptap/tiptap-ui/text-color-button/text-color-button";
import { UndoRedoButton } from "@/components/_organisms/tiptap/tiptap-ui/undo-redo-button";
import { useMentionExtension } from "@/components/_organisms/tiptap/tiptap-ui/use-mention";
import { useMentionSuggestion } from "@/components/_organisms/tiptap/tiptap-ui/use-mention-suggestion";
import { Button, type ButtonSize } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import { Spacer } from "@/components/_organisms/tiptap/tiptap-ui-primitive/spacer";
import { Toolbar, ToolbarGroup, ToolbarSeparator } from "@/components/_organisms/tiptap/tiptap-ui-primitive/toolbar";
import { useUnsafeCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { useMobile } from "@/hooks/use-mobile";
import { useWindowSize } from "@/hooks/use-window-size";
import { cn } from "@/lib/cn";
import { createImageUploadHandler, MAX_FILE_SIZE } from "@/lib/tiptap-img-utils";
import { createPdfUploadHandler, MAX_PDF_FILE_SIZE } from "@/lib/tiptap-pdf-utils";
import { sanitizeTipTapContent } from "@/lib/tiptap-sanitize";
import type { HocuspocusProvider } from "@hocuspocus/provider";
import Collaboration from "@tiptap/extension-collaboration";
import { Highlight } from "@tiptap/extension-highlight";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { Typography } from "@tiptap/extension-typography";
import { Placeholder } from "@tiptap/extensions";
import type { JSONContent } from "@tiptap/react";
import { EditorContent, EditorContext, Extension, useEditor } from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import { yCursorPlugin } from "@tiptap/y-tiptap";

import "tiptap-extension-resizable-image/styles.css";
import "@/components/_organisms/tiptap/tiptap-node/code-block-node/code-block-node.scss";
import "@/components/_organisms/tiptap/tiptap-node/list-node/list-node.scss";
import "@/components/_organisms/tiptap/tiptap-node/image-node/image-node.scss";
import "@/components/_organisms/tiptap/tiptap-node/paragraph-node/paragraph-node.scss";
import "@/components/_organisms/tiptap/tiptap-node/mention-node/mention-node.scss";
import "@/components/_organisms/tiptap/tiptap-templates/simple/simple-editor.scss";

type InitialContent =
  | {
      type: "markdown";
      content: string;
    }
  | {
      type: "html";
      content: string;
    }
  | {
      type: "json";
      content: JSONContent;
    };

export type RichTextDescription = {
  text: string;
  json: JSONContent;
};

export interface CollaborationConfig {
  provider: HocuspocusProvider;
  ydoc: Y.Doc;
  fragmentName?: string;
  userName: string;
  userColor: string;
}

export interface SimpleEditorProps {
  showToolbar?: boolean;
  readOnly?: boolean;
  noPadding?: boolean;
  onChange?: (plainText: string, json: JSONContent) => void;
  initialContent?: InitialContent;
  buttonSize?: ButtonSize;
  isInModal?: boolean;
  placeholder?: string;
  className?: string;
  fullWidth?: boolean;
  toolbarPreset?: "full" | "compact";
  collaboration?: CollaborationConfig;
}

const MainToolbarContent = ({
  onHighlighterClick,
  onLinkClick,
  buttonSize = "base",
  isMobile,
  isInModal,
}: {
  onHighlighterClick: () => void;
  onLinkClick: () => void;
  buttonSize?: ButtonSize;
  isMobile: boolean;
  isInModal: boolean;
  className?: string;
}) => {
  return (
    <>
      <ToolbarGroup>
        <UndoRedoButton action="undo" size={buttonSize} />
        <UndoRedoButton action="redo" size={buttonSize} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3, 4]} size={buttonSize} isInModal={isInModal} />
        <ListDropdownMenu types={["bulletList", "orderedList", "taskList"]} size={buttonSize} />
        <NodeButton type="codeBlock" size={buttonSize} />
        <NodeButton type="blockquote" size={buttonSize} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" size={buttonSize} />
        <MarkButton type="italic" size={buttonSize} />
        <MarkButton type="strike" size={buttonSize} />
        <MarkButton type="code" size={buttonSize} />
        <MarkButton type="underline" size={buttonSize} />
        <FontSizeDropdown size={buttonSize} />
        <LineHeightDropdown size={buttonSize} />
        <TextColorButton size={buttonSize} />
        <BackgroundColorButton size={buttonSize} />
        {!isMobile ? (
          <HighlightPopover size={buttonSize} />
        ) : (
          <HighlighterButton onClick={onHighlighterClick} size={buttonSize} />
        )}
        {!isMobile ? (
          <LinkPopover size={buttonSize} isInModal={isInModal} />
        ) : (
          <LinkButton onClick={onLinkClick} size={buttonSize} />
        )}
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="superscript" size={buttonSize} />
        <MarkButton type="subscript" size={buttonSize} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" size={buttonSize} />
        <TextAlignButton align="center" size={buttonSize} />
        <TextAlignButton align="right" size={buttonSize} />
        <TextAlignButton align="justify" size={buttonSize} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ImageUploadButton size={buttonSize} />
        <PdfUploadButton size={buttonSize} />
      </ToolbarGroup>

      <Spacer />

      {isMobile && <ToolbarSeparator />}
    </>
  );
};

const MobileToolbarContent = ({
  type,
  onBack,
  buttonSize = "base",
}: {
  type: "highlighter" | "link";
  onBack: () => void;
  buttonSize?: ButtonSize;
}) => (
  <>
    <ToolbarGroup>
      <Button data-style="ghost" onClick={onBack} size={buttonSize}>
        <ArrowLeftIcon className="tiptap-button-icon" />
        {type === "highlighter" ? (
          <HighlighterIcon className="tiptap-button-icon" />
        ) : (
          <LinkIcon className="tiptap-button-icon" />
        )}
      </Button>
    </ToolbarGroup>

    <ToolbarSeparator />

    {type === "highlighter" ? <HighlightContent size={buttonSize} /> : <LinkContent size={buttonSize} />}
  </>
);

const CompactToolbarContent = ({ buttonSize = "base", isInModal }: { buttonSize?: ButtonSize; isInModal: boolean }) => (
  <>
    <ToolbarGroup>
      <MarkButton type="bold" size={buttonSize} />
      <MarkButton type="italic" size={buttonSize} />
      <MarkButton type="underline" size={buttonSize} />
      <MarkButton type="strike" size={buttonSize} />
    </ToolbarGroup>

    <ToolbarSeparator />

    <ToolbarGroup>
      <HeadingDropdownMenu levels={[1, 2, 3]} size={buttonSize} isInModal={isInModal} />
      <ListDropdownMenu types={["bulletList", "orderedList", "taskList"]} size={buttonSize} />
    </ToolbarGroup>

    <ToolbarSeparator />

    <ToolbarGroup>
      <LinkPopover size={buttonSize} isInModal={isInModal} />
      <ImageUploadButton size={buttonSize} />
    </ToolbarGroup>

    <Spacer />
  </>
);

export const SimpleEditor = React.forwardRef<HTMLDivElement, SimpleEditorProps>(
  (
    {
      showToolbar = true,
      readOnly = false,
      noPadding = false,
      onChange,
      initialContent,
      buttonSize = "sm",
      isInModal = false,
      placeholder,
      className,
      fullWidth = false,
      toolbarPreset = "full",
      collaboration,
    },
    ref,
  ) => {
    const isMobile = useMobile();
    const windowSize = useWindowSize();
    const [mobileView, setMobileView] = React.useState<"main" | "highlighter" | "link">("main");
    const [rect, setRect] = React.useState<Pick<DOMRect, "x" | "y" | "width" | "height">>({
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    });
    const toolbarRef = React.useRef<HTMLDivElement>(null);
    const mentionExtension = useMentionExtension();

    React.useEffect(() => {
      const updateRect = () => {
        setRect(document.body.getBoundingClientRect());
      };

      updateRect();

      const resizeObserver = new ResizeObserver(updateRect);
      resizeObserver.observe(document.body);

      window.addEventListener("scroll", updateRect);

      return () => {
        resizeObserver.disconnect();
        window.removeEventListener("scroll", updateRect);
      };
    }, []);

    // Unsafe variant on purpose: the editor also renders outside a workspace
    // (public/print views), where uploads simply stay unattributed.
    const workspaceContext = useUnsafeCurrentWorkspaceContext();
    const workspaceId = workspaceContext?.currentWorkspace?.id;
    const uploadImage = React.useMemo(() => createImageUploadHandler({ workspaceId }), [workspaceId]);
    const uploadPdf = React.useMemo(() => createPdfUploadHandler({ workspaceId }), [workspaceId]);

    const conditionalExtensions: Extension[] = [];

    // Set awareness user info in an effect to avoid rebroadcasting on every render
    React.useEffect(() => {
      const awareness = collaboration?.provider?.awareness;
      if (!awareness) return;

      awareness.setLocalStateField("user", {
        name: collaboration.userName,
        color: collaboration.userColor,
      });
    }, [collaboration?.provider?.awareness, collaboration?.userName, collaboration?.userColor]);

    if (collaboration) {
      const awareness = collaboration.provider.awareness;

      conditionalExtensions.push(
        Collaboration.configure({
          document: collaboration.ydoc,
          field: collaboration.fragmentName || "description-fragment",
        }) as Extension,
      );

      if (awareness) {
        const cursorAwareness = awareness;
        const cursorBuilder = (user: Record<string, string>) => {
          const cursor = document.createElement("span");
          cursor.classList.add("collab-cursor");
          cursor.setAttribute("style", `border-color: ${user.color}`);

          const label = document.createElement("span");
          label.classList.add("collab-cursor__label");
          label.setAttribute("style", `background-color: ${user.color}`);
          label.textContent = user.name;
          cursor.appendChild(label);

          return cursor;
        };

        conditionalExtensions.push(
          Extension.create({
            name: "collaborationCursor",
            addProseMirrorPlugins() {
              return [yCursorPlugin(cursorAwareness, { cursorBuilder })];
            },
          }),
        );
      }
    }

    const editor = useEditor({
      immediatelyRender: false,
      shouldRerenderOnTransaction: true,
      editorProps: {
        attributes: {
          autocomplete: "off",
          autocorrect: "off",
          autocapitalize: "off",
          role: "textbox",
          "aria-multiline": "true",
          "aria-label": "Main content area, start typing to enter text.",
        },
      },
      editable: !readOnly,
      extensions: [
        StarterKit.configure({
          link: false,
          // Disable undo/redo when collaboration is enabled (Yjs handles it)
          undoRedo: collaboration ? false : undefined,
        }),
        Link.configure({
          openOnClick: false,
        }),
        TextStyleKit.configure({ fontFamily: false }),
        TextAlign.configure({ types: ["heading", "paragraph"] }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Highlight.configure({ multicolor: true }),
        ResizableImage.extend({
          group: "block",
          inline: false,
        }).configure({
          defaultWidth: 300,
          defaultHeight: 200,
        }),
        Typography,
        Superscript,
        Subscript,
        Placeholder.configure({
          placeholder: placeholder,
          showOnlyWhenEditable: false,
          emptyEditorClass: "is-editor-empty",
        }),

        Selection,
        ImageUploadNode.configure({
          accept: "image/*",
          maxSize: MAX_FILE_SIZE,
          limit: 3,
          upload: uploadImage,
          onError: (error) => console.error("Upload failed:", error),
        }),
        PdfUploadNode.configure({
          accept: "application/pdf",
          maxSize: MAX_PDF_FILE_SIZE,
          limit: 1,
          upload: uploadPdf,
          onError: (error) => console.error("PDF upload failed:", error),
          onSuccess: (url) => console.log("PDF uploaded successfully:", url),
        }),
        mentionExtension,
        ...conditionalExtensions,
      ],
      // When collaboration is enabled, content comes from Yjs document.
      // JSON content is sanitized first — malformed nodes would otherwise crash
      // createDoc and leave the editor unresponsive (onUpdate never fires).
      content: collaboration
        ? undefined
        : initialContent?.type === "json"
          ? sanitizeTipTapContent(initialContent.content)
          : "",
      onUpdate: ({ editor }) => {
        // When collaboration is enabled, Hocuspocus handles persistence via
        // WebSocket — skip the onChange chain to avoid spurious REST mutations
        // on Yjs sync events and mode switches.
        if (onChange && !collaboration) {
          onChange(editor.getText(), editor.getJSON());
        }
      },
      enableContentCheck: true,
      onContentError: ({ error }) => reportTiptapContentError(error),
    });

    // Keep editable state in sync when readOnly changes without remounting
    // (e.g. when collaboration is enabled and the key no longer includes
    // isEditing). Done synchronously during render — toolbar buttons gate
    // their render on editor.isEditable, so deferring this to a useEffect
    // would render them null on the first transition, collapsing the
    // toolbar's auto-visibility heuristic until the parent forces a remount
    // (e.g. by switching tabs and back). The equality guard keeps the call
    // idempotent so any transaction-triggered re-render is a no-op.
    if (editor && editor.isEditable === readOnly) {
      editor.setEditable(!readOnly);
    }

    // Fallback seeding: when collaboration is enabled and the Y.Doc fragment is
    // empty after sync (server-side seed missing or unavailable), populate it
    // from the local initialContent so the existing REST description is not
    // lost when the user toggles the feature flag. Guarded by an awareness
    // peer count of 1 to avoid duplicate seeding from concurrent clients on a
    // brand-new document.
    React.useEffect(() => {
      if (!editor || !collaboration || !initialContent || initialContent.type !== "json") return;

      const fragmentName = collaboration.fragmentName || "description-fragment";
      const fragment = collaboration.ydoc.getXmlFragment(fragmentName);
      if (fragment.length > 0) return;

      const peerCount = collaboration.provider.awareness?.getStates().size ?? 1;
      if (peerCount > 1) return;

      const sanitized = sanitizeTipTapContent(initialContent.content);
      if (!sanitized || !Array.isArray((sanitized as { content?: unknown }).content)) return;

      collaboration.ydoc.transact(() => {
        prosemirrorJSONToYXmlFragment(editor.schema, sanitized, fragment);
      });
    }, [editor, collaboration, initialContent]);

    React.useEffect(() => {
      const checkCursorVisibility = () => {
        if (!editor || !toolbarRef.current) return;

        const { state, view } = editor;
        if (!view.hasFocus()) return;

        const { from } = state.selection;
        const cursorCoords = view.coordsAtPos(from);

        if (windowSize.height < rect.height) {
          if (cursorCoords && toolbarRef.current) {
            const toolbarHeight = toolbarRef.current.getBoundingClientRect().height;
            const isEnoughSpace = windowSize.height - cursorCoords.top - toolbarHeight > 0;

            if (!isEnoughSpace) {
              const scrollY = cursorCoords.top - windowSize.height / 2 + toolbarHeight;
              window.scrollTo({
                top: scrollY,
                behavior: "smooth",
              });
            }
          }
        }
      };

      checkCursorVisibility();
    }, [editor, rect.height, windowSize.height]);

    React.useEffect(() => {
      if (!isMobile && mobileView !== "main") {
        setMobileView("main");
      }
    }, [isMobile, mobileView]);

    const mentionSuggestion = useMentionSuggestion(editor);

    return (
      <EditorContext.Provider value={{ editor }}>
        {showToolbar && !readOnly && (
          <Toolbar
            ref={toolbarRef}
            variant="fixed"
            isInModal={isInModal}
            style={
              isMobile && !isInModal
                ? {
                    bottom: `calc(100% - ${windowSize.height - rect.y}px)`,
                  }
                : {}
            }
          >
            {toolbarPreset === "compact" ? (
              <CompactToolbarContent buttonSize={buttonSize} isInModal={isInModal} />
            ) : mobileView === "main" ? (
              <MainToolbarContent
                onHighlighterClick={() => setMobileView("highlighter")}
                onLinkClick={() => setMobileView("link")}
                isMobile={isMobile}
                buttonSize={buttonSize}
                isInModal={isInModal}
              />
            ) : (
              <MobileToolbarContent
                type={mobileView === "highlighter" ? "highlighter" : "link"}
                onBack={() => setMobileView("main")}
                buttonSize={buttonSize}
              />
            )}
          </Toolbar>
        )}

        <div ref={ref} className={cn("content-wrapper min-h-0 flex-1 ", noPadding ? "" : "py-4")}>
          <EditorContent
            readOnly={readOnly}
            editor={editor}
            role="presentation"
            className={cn(
              "simple-editor-content w-full flex flex-col flex-1 h-full px-6",
              !fullWidth && "max-w-[640px]",
              isInModal && "px-2",
              className,
            )}
          />
        </div>
        {!readOnly && mentionSuggestion}
      </EditorContext.Provider>
    );
  },
);
